// NPM Imports
import React, { useEffect, useRef } from 'react'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
// Component Imports
import HoverMRRDescription from './HoverMRRDescription'
import HoverDifferenceDescription from './HoverDifferenceDescription'
// JS Imports
import {
   //@ts-ignore
   formatFloatAsCurrencySymbol,
   //@ts-ignore
   formatFloatAsPercent,
} from '../../../common/helpers.mjs'

interface ProductRowProps {
   productCode: string
   productEECount: number
   onProductEECountChange: (newCount: number) => void
   productMonthlyCosts: any
}

function ProductRow({
   productCode,
   productEECount,
   onProductEECountChange,
   productMonthlyCosts,
}: ProductRowProps) {
   const state = useQuoteContext()
   const { hasPermission } = useFeaturePermissions()
   const ShowMRROnDifferenceQuotes = hasPermission(
      'ShowMRROnDifferenceQuotes',
      state.user_role,
   )

   return (
      <>
         <div className='col-bhr2'>
            <input
               className='form-control'
               type='number'
               value={
                  productEECount <= 0
                     ? ''
                     : Math.floor(productEECount).toString()
               }
               onChange={(e) =>
                  onProductEECountChange(Math.floor(Number(e.target.value)))
               }
               onBlur={() => onProductEECountChange(Math.floor(productEECount))}
               min={0}
               placeholder={'0'}
               step={1}
            />
         </div>
         <div className='col col-bhr-red'>
            {formatFloatAsPercent(
               productMonthlyCosts?.volumeDiscountedPEPMPercent || 0,
            )}
         </div>
         <div className='col col-bhr-red'></div>
         <div className='col'>
            {(productMonthlyCosts?.discountedPEPMCost || 0) <=
            (productMonthlyCosts?.listPEPMCost || 0)
               ? formatFloatAsCurrencySymbol(
                    productMonthlyCosts?.discountedPEPMCost || 0,
                    state.currencyObject,
                 )
               : ''}
         </div>
         <div className='col'>
            {formatFloatAsCurrencySymbol(
               productMonthlyCosts?.totalPrice || 0,
               state.currencyObject,
            )}
         </div>

         {state.differenceQuote == true ? (
            <>
               <HoverDifferenceDescription
                  placement='bottom'
                  previousPlan={
                     productCode === 'timeTracking'
                        ? state.accountSnowflake.Time_Tracking_Rate_Plan__c
                        : ''
                  }
                  previousQuantity={
                     productCode === 'timeTracking'
                        ? state.accountSnowflake.Billed_Time_Tracking__c
                        : 0
                  }
                  previousInvoicedAmount={
                     productMonthlyCosts?.previousTotalPriceUnconverted
                  }
                  previousStartDate={
                     productCode === 'timeTracking'
                        ? state.accountSnowflake.Time_Tracking_Start_Date__c
                        : ''
                  }
                  previousCurrencyObject={state.currencyObjectPrevious}
               >
                  <span className='hoverable'>
                     {formatFloatAsCurrencySymbol(
                        productMonthlyCosts?.previousTotalPrice,
                        state.currencyObject,
                     )}
                  </span>
               </HoverDifferenceDescription>

               {ShowMRROnDifferenceQuotes ? (
                  <HoverMRRDescription
                     listMRRTotal={productMonthlyCosts.listMRRTotal}
                     previousMRRTotal={productMonthlyCosts.previousMRRTotal}
                     MRRTotal={productMonthlyCosts.MRRTotal}
                     MRRTotalUSD={productMonthlyCosts.MRRTotalUSD}
                     currencyObjectCurrent={state.currencyObject}
                     currencyObjectUSD={state.currencyObjectUSD}
                  >
                     <span className='hoverable'>
                        {formatFloatAsCurrencySymbol(
                           productMonthlyCosts.totalPriceDifference,
                           state.currencyObject,
                        )}
                     </span>
                  </HoverMRRDescription>
               ) : (
                  <span className='col'>
                     {formatFloatAsCurrencySymbol(
                        productMonthlyCosts.totalPriceDifference,
                        state.currencyObject,
                     )}
                  </span>
               )}
            </>
         ) : (
            <></>
         )}
      </>
   )
}

export default function TimeAttendanceProductRow() {
   const state = useQuoteContext()
   const { hasPermission } = useFeaturePermissions()
   const ShowMRROnDifferenceQuotes = hasPermission(
      'ShowMRROnDifferenceQuotes',
      state.user_role,
   )

   // Check if the user has permissions to only see the time attendance product
   const ShowTimeAttendanceOnly = hasPermission(
      'TimeAttendanceOnly',
      state.user_role,
   )

   // Check if the user has permissions to see both the time attendance and time tracking products
   const ShowTimeAttendanceAndTimeTracking = hasPermission(
      'TimeAttendanceAndTimeTracking',
      state.user_role,
   )

   // When the user lacks TimeAttendanceAndTimeTracking permission but already has
   // a non-zero Time Tracking EE count (legacy), surface the radios so they can
   // see/switch between Time Tracking and Time Attendance, defaulting to legacy.
   const hasLegacyTimeTracking =
      !ShowTimeAttendanceAndTimeTracking &&
      (state.productTimeTrackingEECount ?? 0) > 0

   // For new quotes only (no opportunity_id), default the legacy user to
   // 'timeTracking'. When loading an existing opp, respect the saved
   // time_product_selected value — otherwise an opp saved as 'timeAttendance'
   // gets clobbered back to 'timeTracking'. Apply once via a ref so a manual
   // radio change isn't reverted on subsequent renders.
   const legacyDefaultAppliedRef = useRef(false)
   useEffect(() => {
      if (
         hasLegacyTimeTracking &&
         !legacyDefaultAppliedRef.current &&
         !state.opportunity_id
      ) {
         state.setTimeProductSelected('timeTracking')
         legacyDefaultAppliedRef.current = true
      }
   }, [hasLegacyTimeTracking, state.opportunity_id])

   // When the user can only see Time & Attendance (no Time Tracking option),
   // ensure the selected product is always 'timeAttendance' so the calculation
   // produces the correct costs. An opp loaded with 'timeTracking' would
   // otherwise leave selectedProductCosts['timeAttendance'] as all-zeros.
   useEffect(() => {
      if (
         ShowTimeAttendanceOnly &&
         !ShowTimeAttendanceAndTimeTracking &&
         !hasLegacyTimeTracking &&
         state.timeProductSelected !== 'timeAttendance'
      ) {
         state.setTimeProductSelected('timeAttendance')
      }
   }, [
      ShowTimeAttendanceOnly,
      ShowTimeAttendanceAndTimeTracking,
      hasLegacyTimeTracking,
      state.timeProductSelected,
   ])

   if (
      state.product_list['timeTracking']?.NotInPricebook &&
      state.product_list['timeAttendance']?.NotInPricebook
   ) {
      return <> </>
   }

   if (ShowTimeAttendanceAndTimeTracking || hasLegacyTimeTracking) {
      return (
         <div className='row'>
            <div className='col-bhr1' style={{ whiteSpace: 'nowrap' }}>
               <div className='form-check form-check-inline'>
                  {/* Time Tracking */}
                  <input
                     className='form-check-input'
                     type='radio'
                     name='timeProductOptions'
                     id='timeTrackingRadio'
                     value={'timeTracking'}
                     checked={state.timeProductSelected === 'timeTracking'}
                     onChange={state.onTimeProductOptionChange}
                  ></input>
                  <label className='form-check-label'>
                     {state.product_list['timeTracking']['ProductName']}
                  </label>
               </div>
               <div className='form-check form-check-inline'>
                  {/* Time Attendance */}
                  <input
                     className='form-check-input'
                     type='radio'
                     name='timeProductOptions'
                     id='timeAttendanceRadio'
                     value={'timeAttendance'}
                     checked={state.timeProductSelected === 'timeAttendance'}
                     onChange={state.onTimeProductOptionChange}
                  ></input>
                  <label className='form-check-label'>
                     {state.product_list['timeAttendance']['ProductName']}
                  </label>
               </div>
               <span>
                  (
                  {formatFloatAsCurrencySymbol(
                     state.product_list[state.timeProductSelected]?.[
                        'EmpListPrice'
                     ],
                     state.currencyObject,
                     true,
                  )}
                  )
               </span>
            </div>
            <ProductRow
               key={state.timeProductSelected}
               productCode={state.timeProductSelected}
               productEECount={
                  state.timeProductSelected === 'timeTracking'
                     ? state.productTimeTrackingEECount
                     : ((state.dynamicProducts.get(
                          'timeAttendance',
                       ) as number) ?? 0)
               }
               onProductEECountChange={
                  state.timeProductSelected === 'timeTracking'
                     ? state.onProductTimeTrackingEECountChange
                     : (value: number) =>
                          state.setDynamicProduct('timeAttendance', value)
               }
               productMonthlyCosts={
                  state.timeProductSelected === 'timeTracking'
                     ? state.productTimeTrackingMonthlyCosts
                     : state.selectedProductCosts['timeAttendance']
               }
            />
         </div>
      )
   }

   if (ShowTimeAttendanceOnly) {
      const timeAttendanceEECount =
         (state.dynamicProducts.get('timeAttendance') as number) ?? 0
      const timeAttendanceCosts = state.selectedProductCosts['timeAttendance']
      // Return the time attendance product row
      return (
         <div className='row'>
            <div className='col-bhr1'>
               {state.product_list['timeAttendance']['ProductName']} (
               {formatFloatAsCurrencySymbol(
                  state.product_list['timeAttendance']['EmpListPrice'],
                  state.currencyObject,
               )}
               )
            </div>
            <div className='col-bhr2'>
               <input
                  className='form-control'
                  type='number'
                  value={
                     timeAttendanceEECount <= 0
                        ? ''
                        : Math.floor(timeAttendanceEECount).toString()
                  }
                  onChange={(e) =>
                     state.setDynamicProduct(
                        'timeAttendance',
                        Number(e.target.value),
                     )
                  }
                  onBlur={() =>
                     state.setDynamicProduct(
                        'timeAttendance',
                        Math.floor(timeAttendanceEECount),
                     )
                  }
                  min={0}
                  placeholder={'0'}
               />
            </div>
            <div className='col col-bhr-red'>
               {formatFloatAsPercent(
                  timeAttendanceCosts?.volumeDiscountedPEPMPercent || 0,
               )}
            </div>
            <div className='col col-bhr-red'></div>
            <div className='col'>
               {(timeAttendanceCosts?.discountedPEPMCost || 0) <=
               (timeAttendanceCosts?.listPEPMCost || 0)
                  ? formatFloatAsCurrencySymbol(
                       timeAttendanceCosts?.discountedPEPMCost || 0,
                       state.currencyObject,
                    )
                  : ''}
            </div>
            <div className='col'>
               {formatFloatAsCurrencySymbol(
                  timeAttendanceCosts?.totalPrice,
                  state.currencyObject,
               )}
            </div>
            {/* TODO: Update this to handle both time tracking and time attendance */}
            {state.differenceQuote == true ? (
               <>
                  <HoverDifferenceDescription
                     placement='bottom'
                     previousPlan={''}
                     previousQuantity={0}
                     previousInvoicedAmount={
                        timeAttendanceCosts?.previousTotalPriceUnconverted
                     }
                     previousStartDate={''}
                     previousCurrencyObject={state.currencyObjectPrevious}
                  >
                     <span className='hoverable'>
                        {formatFloatAsCurrencySymbol(
                           timeAttendanceCosts?.previousTotalPrice,
                           state.currencyObject,
                        )}
                     </span>
                  </HoverDifferenceDescription>
                  {ShowMRROnDifferenceQuotes ? (
                     <HoverMRRDescription
                        listMRRTotal={timeAttendanceCosts?.listMRRTotal}
                        previousMRRTotal={timeAttendanceCosts?.previousMRRTotal}
                        MRRTotal={timeAttendanceCosts?.MRRTotal}
                        MRRTotalUSD={timeAttendanceCosts?.MRRTotalUSD}
                        currencyObjectCurrent={state.currencyObject}
                        currencyObjectUSD={state.currencyObjectUSD}
                     >
                        <span className='hoverable'>
                           {formatFloatAsCurrencySymbol(
                              timeAttendanceCosts?.totalPriceDifference,
                              state.currencyObject,
                           )}
                        </span>
                     </HoverMRRDescription>
                  ) : (
                     <span className='col'>
                        {formatFloatAsCurrencySymbol(
                           timeAttendanceCosts?.totalPriceDifference,
                           state.currencyObject,
                        )}
                     </span>
                  )}
               </>
            ) : (
               <></>
            )}
         </div>
      )
   }

   return <></>
}
